import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { PublicHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { apiClient, auth } from "@/lib/api";
import { TIDS } from "@/lib/testIds";
import { Smartphone, ShieldCheck } from "lucide-react";

/**
 * Unified SMS-OTP sign-in for admins AND employees. The server locates the
 * account by mobile (admin first, then employee) and returns the correct role.
 */
export default function TeamLogin() {
  const navigate = useNavigate();
  const [mobile, setMobile] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hint, setHint] = useState("");

  const sendOtp = async (e) => {
    e?.preventDefault();
    if (!mobile.trim()) return toast.error("Enter your mobile number");
    setLoading(true);
    try {
      const res = await apiClient.post("/auth/team/send-otp", { mobile });
      if (res.data?.sent) {
        toast.success("Code sent via SMS");
        setHint("");
      } else {
        toast.message("SMS not sent — use dev code", { description: res.data?.dev_hint || "Try 000000" });
        setHint(res.data?.dev_hint || "Dev bypass code: 000000");
      }
      setStep(2);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send code");
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    if (!code.trim()) return toast.error("Enter the 6-digit code");
    setLoading(true);
    try {
      const res = await apiClient.post("/auth/team/verify-otp", { mobile, code });
      const role = res.data.role;
      auth.setSession(res.data.access_token, role, res.data.user);
      toast.success("Welcome back");
      navigate(role === "admin" ? "/admin/dashboard" : "/employee/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid code");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader />
      <div className="mx-auto max-w-md px-6 py-16 md:py-24">
        <div className="text-center mb-8">
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-900 text-white mx-auto">
            {step === 1 ? <Smartphone className="h-6 w-6" /> : <ShieldCheck className="h-6 w-6" />}
          </span>
          <h1 className="mt-5 text-3xl font-heading font-medium tracking-tight text-slate-900">
            {step === 1 ? "Team sign-in" : "Enter verification code"}
          </h1>
          <p className="mt-2 text-slate-600 text-sm">
            {step === 1
              ? "Admins and employees — sign in with your mobile number."
              : `Code sent to ${mobile}. It expires in 10 minutes.`}
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-7">
          {step === 1 ? (
            <form onSubmit={sendOtp} data-testid={TIDS.teamLoginForm} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="tmobile">Mobile number</Label>
                <Input
                  id="tmobile"
                  type="tel"
                  placeholder="+1 555 123 4567"
                  data-testid={TIDS.teamLoginMobile}
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value)}
                  autoComplete="tel"
                  required
                />
                <p className="text-xs text-slate-500">Must match the mobile number your admin has on file.</p>
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid={TIDS.teamLoginSendOtp}
                className="w-full rounded-full bg-slate-900 hover:bg-slate-800 text-white h-11"
              >
                {loading ? "Sending…" : "Send code"}
              </Button>
            </form>
          ) : (
            <form onSubmit={verify} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="tcode">6-digit code</Label>
                <Input
                  id="tcode"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="123456"
                  data-testid={TIDS.teamLoginCode}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className="text-2xl tracking-[0.5em] text-center font-mono"
                  required
                />
                {hint ? <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">{hint}</p> : null}
              </div>
              <Button
                type="submit"
                disabled={loading}
                data-testid={TIDS.teamLoginVerify}
                className="w-full rounded-full bg-slate-900 hover:bg-slate-800 text-white h-11"
              >
                {loading ? "Verifying…" : "Verify & sign in"}
              </Button>
              <button
                type="button"
                onClick={() => { setStep(1); setCode(""); }}
                className="w-full text-sm text-slate-500 hover:text-slate-800 mt-2"
              >
                Change number
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-sm text-slate-500 mt-6">
          Referral partner? <Link to="/partner/login" className="text-blue-600 hover:underline font-medium">Sign in here</Link>
        </p>
      </div>
    </div>
  );
}
